<script lang="ts" setup>
import { useVModel } from "@vueuse/core"
import { useField, useForm } from "vee-validate"
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import * as yup from "yup"

type Props = {
    modelValue: { start: number; end: number }[]
    /**
     * Label du sélecteur
     */
    label?: string
    /**
     * Liste des périodes de jour/heures désactivées
     */
    disabledPeriods?: { start: number; end: number }[]
    /**
     * Message d'aide
     */
    hint?: string
    /**
     * Indique si le champ est désactivé
     */
    disabled?: boolean
    /**
     * Message d'erreur
     */
    error?: string
    /**
     * Id de l'input
     */
    inputId?: string
    /**
     * Indique si le champ est requis
     */
    required?: boolean
    /**
     * Ajoute la période si l'utilisateur clique en dehors du formulaire et que les champs
     * "startTime" et "endTime" sont valides
     */
    autoSelect?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    label: "",
    disabled: false,
    disabledPeriods: () => [],
    hint: "",
    error: "",
    inputId: "period-select",
    required: false,
    autoSelect: false,
})
const emit = defineEmits(["update:modelValue", "click:remove"])

//=============================================================================//
//                                  Lang
//=============================================================================//
const { t } = useI18n({
    messages: {
        fr: {
            period: `{start} - {end}`,
            start: "Début",
            end: "Fin",
            all_day: "Toute la journée",
            tooltip_add: "Ajouter la période",
            tooltip_all_day:
                "Vous ne pouvez pas sélectionner toute la journée\ncar il existe des périodes d'heures personnalisées pour une même journé.",
            content: {
                empty: "Pas de période renseignée.",
            },
            error: {
                empty: "Veuillez renseigner au moins une période.",
                form: "Veuillez corriger les erreurs dans le formulaire.",
                period_already_selected:
                    "La période renseignée est déjà présente dans la liste des périodes sélectionnées.",
                period_already_disabled:
                    "La période renseignée chevauche une période non disponible.",
                period_all_day_disabled:
                    "Vous ne pouvez pas sélectionner de périodes d'heures personnalisées\ncar pour une même journée la période de toute la journée est déjà sélectionnée.",
            },
        },
    },
})
// ============================================================================//
// Gestion de l'input
// ============================================================================//
/**
 * Liste des périodes sélectionnées
 */
const periods = useVModel(props, "modelValue", emit, {
    // utilise un watch pour surveiller les changements dans la liste des périodes sélectionnées
    passive: true,
    // Surveille les changements profonds dans la liste des périodes sélectionnées pour déclencher le emit "update:modelValue"
    deep: true,
    eventName: "update:modelValue",
    // il faut cloner la props pour éviter de crée un problème de props mutation.
    // Comme la valeur est un tableau, la référence est passée et les modifications sont répercutées. En clonant, on évite ce problème.
    clone: true,
    defaultValue: [],
})

/**
 * Indique si le champ est invalide
 */
const isInvalid = ref<boolean>(false)

const getGeneralErrorMessage = computed(() => {
    if (isInvalid.value) {
        return t("error.empty")
    }
    if (Object.keys(errors.value).length > 0) {
        return t("error.form")
    }
    return props.error
})

const hasErrorOnSelect = computed(() => {
    return (
        isInvalid.value || !!props.error || !!Object.keys(errors.value).length
    )
})

function onHideSelect() {
    if (!isSubmitFormDisabled.value && props.autoSelect) {
        onSubmitAddTime()
    }
}

defineExpose({
    /**
     * Fonction permettant de lancer la validation du composant.
     * Le composant est valide si les champs du formulaire le sont et
     * si le composant est marqué comme required alors il faut au moins une période renseignée.
     * @returns true si le composant est valide, false sinon
     */
    validate: async () => {
        await validateField("startTime")
        await validateField("endTime")
        // si la liste des périodes est vide et que le champ est requis
        if (!(periods.value && periods.value.length > 0) && props.required) {
            isInvalid.value = true
        }
        return (
            !isInvalid.value && !errors.value.startTime && !errors.value.endTime
        )
    },
})

// ============================================================================//
// Gestion des périodes sélectionnées
// ============================================================================//

/**
 * Retire la période renseignée en paramètre de la liste des périodes renseignées
 * @param index Index de l'élément à retirer
 */
function onRemovePeriodFromChipList(index: number) {
    if (!periods.value) return
    periods.value.splice(index, 1)
}
/**
 * Permet de formater un nombre en heure au format HH:00
 * @param hour - Heure à formater de 0 à 23
 */
function formatToHour(hour: number | undefined) {
    if (hour === undefined || hour === null || hour < 0 || hour > 23)
        return "Heure invalide"
    return hour.toString().padStart(2, "0") + ":00"
}

const isAllDays = computed(() => {
    return (
        periods.value.length === 1 &&
        periods.value[0].start === 0 &&
        periods.value[0].end === 0
    )
})

const hasOverlappingDisabledPeriodsAllDay = computed(() => {
    return (
        props.disabledPeriods.some(
            (disabled) => disabled.start === 0 && disabled.end === 0
        ) &&
        !periods.value.some((period) => period.start === 0 && period.end === 0)
    )
})

function onToggleAllDays(value: boolean) {
    if (value) {
        periods.value = [{ start: 0, end: 0 }]
    } else {
        periods.value = []
    }
}

// ============================================================================//
// Gestion du formulaire
// ============================================================================//

const schema = yup.object({
    startTime: yup
        .date()
        .nullable()
        .test(
            "start-required-if-end",
            "Une heure de début est requise car une heure de fin est définie.",
            function (value) {
                const { endTime } = this.parent
                return !endTime || value != null
            }
        )
        .test(
            "start-max-end",
            "L'heure de début doit être inférieure à l'heure de fin.",
            function (value) {
                const { endTime } = this.parent
                if (!value || !endTime) return true

                // Dans le cas où l'heure de fin est minuit et l'heure de début n'est pas minuit
                // alors il n'y a pas de d'erreur
                if (value.getHours() !== 0 && endTime.getHours() === 0)
                    return true

                return value.getHours() < endTime.getHours()
            }
        )
        .test(
            "no-disabled-period",
            "La période sélectionnée chevauche une période non disponible. ",
            function (value) {
                const { endTime } = this.parent
                if (!value || !endTime || !props.disabledPeriods) return true

                const startHour = value.getHours()
                const endHour = endTime.getHours()

                // si la période désactivé correspond à toute la journée alors on ne vérifie pas les heures et on retourne false
                if (
                    props.disabledPeriods.some(
                        (disabled) => disabled.start === 0 && disabled.end === 0
                    )
                ) {
                    return false
                }

                // si dans les périodes désactivées il y a une période de type endTime = 0 et startTime != 0 alors on ne bloque pas
                if (
                    props.disabledPeriods.some(
                        (disabled) => disabled.end === 0 && disabled.start !== 0
                    )
                ) {
                    return (
                        startHour < props.disabledPeriods[0].start &&
                        endHour <= props.disabledPeriods[0].start
                    )
                }

                return !props.disabledPeriods.some(
                    (disabled) =>
                        startHour <= disabled.start && endHour > disabled.end
                )
            }
        ),

    endTime: yup
        .date()
        .nullable()
        .test(
            "end-required-if-start",
            "Une heure de fin est requise car une heure de début est définie.",
            function (value) {
                const { startTime } = this.parent
                return !startTime || value != null
            }
        )
        .test(
            "end-min-start",
            "L'heure de fin doit être supérieure à l'heure de début.",
            function (value) {
                const { startTime } = this.parent
                if (!value || !startTime) return true

                // Dans le cas où l'heure de fin est minuit et l'heure de début n'est pas minuit
                // alors il n'y a pas de d'erreur
                if (value.getHours() === 0 && startTime.getHours() !== 0)
                    return true

                return value.getHours() > startTime.getHours()
            }
        )
        .test(
            "no-overlap",
            "La période sélectionnée chevauche une période non disponible. ",
            function (value) {
                const { startTime } = this.parent
                if (!value || !startTime || !props.disabledPeriods) return true

                const startHour = startTime.getHours()
                const endHour = value.getHours()

                // si la période désactivé correspond à toute la journée alors on ne vérifie pas les heures et on retourne false
                if (
                    props.disabledPeriods.some(
                        (disabled) => disabled.start === 0 && disabled.end === 0
                    )
                ) {
                    return false
                }

                // si dans les périodes désactivées il y a une période de type endTime = 0 et startTime != 0, alors on vérifie si la période renseignée chevauche la période désactivée
                if (
                    props.disabledPeriods.some(
                        (disabled) => disabled.end === 0 && disabled.start !== 0
                    )
                ) {
                    return (
                        startHour < props.disabledPeriods[0].start &&
                        endHour <= props.disabledPeriods[0].start
                    )
                }

                return !props.disabledPeriods.some(
                    (disabled) =>
                        startHour <= disabled.start && endHour > disabled.end
                )
            }
        ),
})
const { errors, validateField } = useForm({
    validationSchema: schema,
})
const { value: startTime } = useField<Date | undefined>("startTime")
const { value: endTime } = useField<Date | undefined>("endTime")
/**
 * Ajoute les variables de début de période et de fin de période récupérées dans
 * le datePicker et les ajoute dans la liste des périodes renseignés
 */
async function onSubmitAddTime() {
    // Valide les champs
    const isStartTimeValid = await validateField("startTime")
    const isEndTimeValid = await validateField("endTime")

    if (
        !isStartTimeValid.valid ||
        !isEndTimeValid.valid ||
        !endTime.value ||
        !startTime.value
    ) {
        return
    }
    periods.value?.push({
        start: startTime.value?.getHours(),
        end: endTime.value?.getHours(),
    })
    startTime.value = undefined
    endTime.value = undefined
}

/**
 * Indique si des erreurs sont présentes sur les champs du formulaire
 */
const hasErrorsOnTime = computed(() => {
    if (hasOverlappingDisabledPeriodsAllDay.value && !isAllDays.value) {
        return true
    }

    return (
        !!errors.value.startTime ||
        !!errors.value.endTime ||
        isPeriodAlreadyDisabled.value ||
        isPeriodAlreadySelected.value
    )
})

/**
 * Récupère le bon message d'erreur à afficher
 */
const getErrorMessage = computed(() => {
    if (hasOverlappingDisabledPeriodsAllDay.value) {
        return t("error.period_all_day_disabled")
    }

    if (isPeriodAlreadySelected.value) {
        return t("error.period_already_selected")
    } else if (isPeriodAlreadyDisabled.value) {
        return t("error.period_already_disabled")
    }

    return errors.value.startTime || errors.value.endTime || null
})

/**
 * Indique si la période renseignée est déjà présente dans les choix
 */
const isPeriodAlreadySelected = computed(() => {
    if (
        periods.value &&
        periods.value.some(
            (period) =>
                period.start === startTime.value?.getHours() &&
                period.end === endTime.value?.getHours()
        )
    ) {
        return true
    }
    return false
})

/**
 * Indique si la période renseignée chevauche une période désactivée
 */
const isPeriodAlreadyDisabled = computed(() => {
    if (
        props.disabledPeriods.some((disabled) => {
            const startHour = startTime.value?.getHours()
            const endHour = endTime.value?.getHours()

            if (!startHour || !endHour) return false

            return startHour < disabled.end && endHour > disabled.start
        })
    ) {
        return true
    }
    return false
})

function isPeriodInvalid(period: { start: number; end: number }) {
    if (period.start !== 0 && period.end === 0) {
        return false
    }

    if (period.start > period.end) {
        return true
    }
    return false
}

/**
 * Indique si le bouton pour ajouter une période est désactivé ou non.
 * Le bouton est désactivé si les champs de temps ne sont pas valides
 * ou que la période est déjà présente dans les choix où dans les périodes désactivées
 */
const isSubmitFormDisabled = computed(() => {
    return hasErrorsOnTime.value || !startTime.value || !endTime.value
})

watch(
    () => ({ ...periods.value }), // Surveille les changements dans selectedPeriod
    (newVal, oldVal) => {
        // Convertir les proxys en tableaux natifs si nécessaire
        const newArray = Array.isArray(newVal) ? newVal : Object.values(newVal)
        const oldArray = Array.isArray(oldVal) ? oldVal : Object.values(oldVal)

        if (
            Array.isArray(oldArray) &&
            oldArray.length > 0 && // Vérifie que l'ancienne valeur n'est pas vide
            (!newArray || newArray.length === 0) // Vérifie que la nouvelle valeur est vide ou undefined
        ) {
            isInvalid.value = true // Marquer comme invalide
        } else {
            isInvalid.value = false // Sinon, marquer comme valide
        }
    },
    { deep: true }
)
</script>

<template>
    <BaseInputWrapper
        variant="on"
        :label="label"
        :input-id="inputId"
        :hint="hint"
        :error="getGeneralErrorMessage"
        class="base-multi-select-period"
    >
        <BaseSelect
            class="base-multi-select-period__select-input"
            :id="inputId"
            option-label="start"
            empty-message=""
            :invalid="hasErrorOnSelect"
            :disabled="disabled"
            display="chip"
            show-clear
            pt:list-container:style="display: none"
            :class="periods?.length && 'p-inputwrapper-filled'"
            @hide="onHideSelect"
        >
            <!-- Liste des périodes sélectionnées -->
            <template #value>
                <div
                    class="base-multi-select-period__value"
                    v-if="periods?.length"
                >
                    <div v-for="(item, index) in periods" :key="index">
                        <BaseChip
                            removable
                            class="base-multi-select-period__chip-item"
                            :data-error="isPeriodInvalid(item)"
                        >
                            <span v-if="!isAllDays">
                                {{
                                    t("period", {
                                        start: formatToHour(item.start),
                                        end: formatToHour(item.end),
                                    })
                                }}
                            </span>
                            <div v-else>
                                {{ t("all_day") }}
                            </div>
                            <BaseBadge
                                class="base-multi-select-period__chip-error-badge"
                                v-if="isPeriodInvalid(item)"
                                value=""
                                severity="danger"
                            />

                            <!-- Met dans le template l'îcone de suppression pour éviter bug de double suppression dans l'UI -->
                            <template #removeicon>
                                <i
                                    class="pi pi-times-circle"
                                    @click="onRemovePeriodFromChipList(index)"
                                ></i>
                            </template>
                        </BaseChip>
                    </div>
                </div>
            </template>
            <!-- En tête du dropdown -->
            <template #header>
                <div class="base-multi-select-period__header">
                    <!-- Formulaire : timepickers + boutton ajouter-->
                    <form
                        @submit.prevent.stop="onSubmitAddTime"
                        class="base-multi-select-period__form"
                    >
                        <div class="base-multi-select-period__form-all-day">
                            <BaseToggleSwitch
                                input-id="all-day"
                                :model-value="isAllDays"
                                :disabled="hasOverlappingDisabledPeriodsAllDay"
                                @update:model-value="onToggleAllDays"
                            ></BaseToggleSwitch>
                            <label for="all-days">{{ t("all_day") }} </label>
                            <i
                                v-if="hasOverlappingDisabledPeriodsAllDay"
                                class="pi pi-info-circle"
                                v-tooltip.bottom="t('tooltip_all_day')"
                            ></i>
                        </div>
                        <div class="base-multi-select-period__form-periods">
                            <!-- Heure de début -->
                            <BaseInputWrapper
                                input-id="start-time"
                                :label="t('start')"
                            >
                                <BaseDatePicker
                                    class="base-multi-select-period__date-picker"
                                    v-model="startTime"
                                    name="startTime"
                                    :step-minute="60"
                                    :disabled="
                                        isAllDays ||
                                        hasOverlappingDisabledPeriodsAllDay
                                    "
                                    :invalid="
                                        !!errors.startTime ||
                                        isPeriodAlreadySelected ||
                                        isPeriodAlreadyDisabled
                                    "
                                    time-only
                                    :pt="{
                                        minutepicker:
                                            'datepicker-minute-picker-hide',
                                    }"
                                    v-mask="'99:00'"
                                />
                            </BaseInputWrapper>
                            <!-- Heure de fin -->
                            <BaseInputWrapper
                                input-id="end-time"
                                :label="t('end')"
                            >
                                <BaseDatePicker
                                    class="base-multi-select-period__date-picker"
                                    v-model="endTime"
                                    name="endTime"
                                    :step-minute="60"
                                    :disabled="
                                        isAllDays ||
                                        hasOverlappingDisabledPeriodsAllDay
                                    "
                                    :invalid="
                                        !!errors.endTime ||
                                        isPeriodAlreadySelected ||
                                        isPeriodAlreadyDisabled
                                    "
                                    time-only
                                    :pt="{
                                        minutepicker:
                                            'datepicker-minute-picker-hide',
                                    }"
                                    v-mask="'99:00'"
                                />
                            </BaseInputWrapper>
                            <!-- Bouton ajouter -->
                            <BaseButton
                                type="submit"
                                severity="secondary"
                                text
                                rounded
                                :disabled="
                                    isSubmitFormDisabled ||
                                    isAllDays ||
                                    hasOverlappingDisabledPeriodsAllDay
                                "
                                size="small"
                                icon="pi pi-plus"
                                v-tooltip.left="t('tooltip_add')"
                            />
                        </div>
                    </form>
                    <hr
                        class="border border-slate-200"
                        v-if="hasErrorsOnTime"
                    />
                    <BaseMessage
                        v-if="hasErrorsOnTime"
                        severity="error"
                        icon="pi pi-times-circle"
                        class="base-multi-select-period__error-message"
                    >
                        <span class="whitespace-pre-wrap">
                            {{ getErrorMessage }}
                        </span>
                    </BaseMessage>
                    <slot name="error"></slot>
                </div>
            </template>
        </BaseSelect>
    </BaseInputWrapper>
</template>

<style scoped>
.base-multi-select-period {
    /* fix la hauteur pour éviter la hauteur dynamique quand on ajoute ou retire des périodes */
    height: 2.68rem;
    /* réduit la taille du padding si des chips sont présentes */
    :deep(.p-select-label:has(.p-chip)) {
        padding: calc(var(--p-select-padding-y) / 2)
            calc(var(--p-select-padding-x) / 2);
    }
    /* Permet de garder le label en haut de l'input quand des chips sont présents */
    :deep(.p-floatlabel-on:has(.p-inputwrapper-filled) label) {
        top: 0;
        transform: translateY(-50%);
        border-radius: var(--p-floatlabel-on-border-radius);
        background: var(--p-floatlabel-on-active-background);
        padding: var(--p-floatlabel-on-active-padding);
    }
}
/* Liste des périodes sélectionnées affichées dans l'input */
.base-multi-select-period__value {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    gap: 0.25rem;
}
.base-multi-select-period__chip-item {
    position: relative;
    &[data-error="true"] {
        border: 1px solid var(--p-red-500);
    }
    padding: 0.25rem 0.75rem;
}
.base-multi-select-period__chip-error-badge {
    position: absolute;
    top: 0;
    right: 0;
}
.base-multi-select-period__header {
    max-width: 31rem;
}
/* Le formulaire d'une période */
.base-multi-select-period__form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background-color: rgb(248 250 252);
    padding: 0.75rem;

    .base-multi-select-period__form-periods {
        display: flex;
        flex-direction: row;
        gap: 0.75rem;
        .p-button {
            height: 2.5rem;
            min-width: 2.5rem;
        }
    }
    .base-multi-select-period__form-all-day {
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
        align-items: center;
    }
}
/* Le message d'erreur affiché */
.base-multi-select-period__error-message {
    margin: 1rem;
    text-wrap: wrap;
    padding: 0.5rem;
}
</style>
<style>
.datepicker-minute-picker-hide {
    .p-button {
        display: none !important;
    }
}
</style>
